import React, { useState } from "react";
import { TrendingUp, TrendingDown, CheckCircle } from "lucide-react";

type Sentiment = "too_high" | "looks_right" | "too_low";

const SENTIMENT_OPTIONS: {
  value: Sentiment;
  icon: React.ReactNode;
  label: string;
  description: string;
}[] = [
  {
    value: "too_low",
    icon: <TrendingUp className="h-5 w-5" />,
    label: "Price higher",
    description: "I think we could price higher",
  },
  {
    value: "looks_right",
    icon: <CheckCircle className="h-5 w-5" />,
    label: "Looks right",
    description: "Price looks right to me",
  },
  {
    value: "too_high",
    icon: <TrendingDown className="h-5 w-5" />,
    label: "Too high",
    description: "Price seems too high",
  },
];

function sentimentStyle(
  value: Sentiment,
  selected: boolean
): React.CSSProperties {
  if (!selected) return {};
  if (value === "too_low") return { background: "#dcfce7", borderColor: "#16a34a", color: "#15803d" };
  if (value === "looks_right") return { background: "#dbeafe", borderColor: "#2563eb", color: "#1d4ed8" };
  return { background: "#fef3c7", borderColor: "#d97706", color: "#b45309" };
}

interface CMASellerFeedbackProps {
  cmaId: string;
  initialSentiment: string | null;
  primaryColor: string;
}

export function CMASellerFeedback({
  cmaId,
  initialSentiment,
  primaryColor,
}: CMASellerFeedbackProps) {
  const validInitial = SENTIMENT_OPTIONS.some((opt) => opt.value === initialSentiment)
    ? (initialSentiment as Sentiment)
    : null;
  const [selected, setSelected] = useState<Sentiment | null>(validInitial);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(!!validInitial);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!selected) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const res = await fetch(
        `${supabaseUrl}/functions/v1/submit-cma-feedback`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: supabaseKey,
            Authorization: `Bearer ${supabaseKey}`,
          },
          body: JSON.stringify({ cmaId, sentiment: selected, comment: comment || undefined }),
        }
      );
      const data = await res.json();
      if (res.status === 409) {
        setSubmitted(true);
        return;
      }
      if (!res.ok || data.error) throw new Error(data.error || data.message || `Server error (${res.status})`);
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 md:p-8 text-center">
        <CheckCircle className="h-8 w-8 text-emerald-500 mx-auto mb-3" />
        <p className="font-semibold text-slate-800 text-lg">Thanks for your feedback!</p>
        <p className="text-sm text-slate-400 mt-1">Your agent has been notified.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 md:p-8">
      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-400 mb-2">
        What do you think of the price?
      </p>
      <p className="text-slate-600 text-sm mb-6">
        Your feedback helps your agent fine-tune the recommendation.
      </p>

      <div className="grid grid-cols-3 gap-3 mb-4">
        {SENTIMENT_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setSelected(opt.value)}
            className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all text-sm font-medium ${
              selected === opt.value
                ? "hover:opacity-90"
                : "hover:bg-slate-50 hover:border-slate-300"
            }`}
            style={
              selected === opt.value
                ? sentimentStyle(opt.value, true)
                : { borderColor: "#e2e8f0", color: "#64748b" }
            }
          >
            {opt.icon}
            <span className="text-xs leading-tight text-center">{opt.description}</span>
          </button>
        ))}
      </div>

      {selected && (
        <div className="space-y-3">
          <textarea
            className="w-full rounded-lg border border-slate-200 p-3 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 resize-none"
            rows={3}
            placeholder="Any additional thoughts? (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          {error && <p className="text-sm text-red-500">{error}</p>}
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full py-2.5 px-4 rounded-lg text-sm font-semibold text-white transition-opacity disabled:opacity-60"
            style={{ backgroundColor: primaryColor }}
          >
            {isSubmitting ? "Submitting…" : "Submit Feedback"}
          </button>
        </div>
      )}
    </div>
  );
}
